import { db, auth } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Get the current user ID from Firebase Auth
const getUserId = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user.uid;
};

// Get thoughts from the past N days
export const getRecentThoughts = async (days = 7) => {
  try {
    const userId = getUserId();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    const cutoffTimestamp = cutoffDate.toISOString();

    const thoughtsRef = collection(db, 'thoughts');
    const q = query(
      thoughtsRef,
      where('userId', '==', userId),
      where('timestamp', '>=', cutoffTimestamp)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting recent thoughts:', error);
    return [];
  }
};

// Get tag usage statistics from thoughts
export const getTagStats = async (thoughts) => {
  try {
    // Initialize tag counters and importance sums
    const tagStats = {};
    let totalImportance = 0;
    
    // Process each thought
    thoughts.forEach(thought => {
      if (thought.tags && thought.tags.length > 0) {
        const importance = thought.importance || 0;
        totalImportance += importance;
        
        // Add importance to each tag
        thought.tags.forEach(tag => {
          if (!tagStats[tag]) {
            tagStats[tag] = {
              importance: 0,
              count: 0
            };
          }
          tagStats[tag].importance += importance;
          tagStats[tag].count += 1;
        });
      }
    });
    
    // Convert to array for easier sorting
    const tagStatsArray = Object.keys(tagStats).map(tag => ({
      tag,
      count: tagStats[tag].count,
      importance: tagStats[tag].importance,
      percentage: totalImportance > 0 
        ? Math.round((tagStats[tag].importance / totalImportance) * 100) 
        : 0
    }));
    
    // Sort by importance descending
    return tagStatsArray.sort((a, b) => b.importance - a.importance);
  } catch (error) {
    console.error('Error calculating tag stats:', error);
    return [];
  }
};

// Get daily thought distribution for the timeframe
export const getWeeklyDistribution = async (thoughts, days = 7) => {
  try {
    // Create an array of dates for the selected range
    const dateLabels = [];
    const dateCounts = {};
    
    // Generate dates for the selected range
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());  // Local midnight today
    
    for (let i = 0; i < days; i++) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD format

      // For display formatting
      const displayDate = new Date(date).toLocaleDateString('en-US', {
        month: 'short', 
        day: 'numeric'
      });
      
      // Add in reverse so oldest is first
      dateLabels.unshift({
        dateString,
        displayDate
      });
      
      // Initialize count to 0
      dateCounts[dateString] = 0;
    }
    
    // Count thoughts for each date
    thoughts.forEach(thought => {
      if (thought.timestamp) {
        const date = new Date(thought.timestamp);
        const localDateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        if (dateCounts[localDateString] !== undefined) {
          dateCounts[localDateString]++;
        }
      }
    });
    
    // Create the distribution data
    return dateLabels.map(({dateString, displayDate}) => ({
      date: dateString,
      displayDate,
      count: dateCounts[dateString]
    }));
  } catch (error) {
    console.error('Error calculating daily distribution:', error);
    return [];
  }
};

// Get daily writing streak
export const getWritingStreak = async (allThoughts) => {
  try {
    // Get all user thoughts
    const thoughts = allThoughts || await getAllUserThoughts();
    
    if (!thoughts.length) return 0;
    
    // Create a map of dates when user wrote thoughts
    const writingDates = new Set();
    thoughts.forEach(thought => {
      if (thought.timestamp) {
        const date = new Date(thought.timestamp);
        writingDates.add(date.toDateString());
      }
    });
    
    // Calculate current streak
    let streak = 0;
    const today = new Date();
    let currentDate = new Date(today);
    
    // Check backward from today
    while (writingDates.has(currentDate.toDateString())) {
      streak++;
      currentDate.setDate(currentDate.getDate() - 1);
    }
    
    return streak;
  } catch (error) {
    console.error('Error calculating writing streak:', error);
    return 0;
  }
};

// Get all user thoughts
export const getAllUserThoughts = async () => {
  try {
    const userId = getUserId();
    
    const thoughtsRef = collection(db, 'thoughts');
    const q = query(
      thoughtsRef,
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all thoughts:', error);
    return [];
  }
};

// Get cumulative importance distribution
export const getImportanceDistribution = async (allThoughts, days = 7) => {
  try {
    // Create an array of dates for the selected range
    const dateLabels = [];
    
    // Generate dates for the selected range
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());  // Local midnight today
    
    // Calculate the starting date for our timeframe
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (days - 1));

    // Sort all thoughts by date
    const sortedThoughts = [...allThoughts].sort((a, b) => {
      if (!a.timestamp || !b.timestamp) return 0;
      return new Date(a.timestamp) - new Date(b.timestamp);
    });

    // Calculate cumulative sum up to each date
    let runningSum = 0;
    const dailySums = new Map();

    sortedThoughts.forEach(thought => {
      if (!thought.timestamp) return;
      const thoughtDate = new Date(thought.timestamp);
      const thoughtDateString = `${thoughtDate.getFullYear()}-${String(thoughtDate.getMonth() + 1).padStart(2, '0')}-${String(thoughtDate.getDate()).padStart(2, '0')}`;
      
      runningSum += (thought.importance || 0);
      dailySums.set(thoughtDateString, runningSum);
    });

    // Generate dates for the selected range
    for (let i = 0; i < days; i++) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD format

      // For display formatting
      const displayDate = new Date(date).toLocaleDateString('en-US', {
        month: 'short', 
        day: 'numeric'
      });
      
      // Add in reverse so oldest is first
      dateLabels.unshift({
        dateString,
        displayDate
      });
    }
    
    // Create the distribution data with cumulative sums
    return dateLabels.map(({dateString, displayDate}) => {
      // Find the last sum that occurred on or before this date
      let cumulativeImportance = 0;
      for (const [date, sum] of dailySums.entries()) {
        if (date <= dateString) {
          cumulativeImportance = sum;
        }
      }

      return {
        date: dateString,
        displayDate,
        cumulativeImportance
      };
    });
  } catch (error) {
    console.error('Error calculating importance distribution:', error);
    return [];
  }
};

// Get milestone thoughts for chart annotations
export const getMilestoneThoughts = async (allThoughts, days = 7) => {
  try {
    // Filter for milestone thoughts within the timeframe
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    const cutoffTimestamp = cutoffDate.toISOString();
    
    const milestoneThoughts = allThoughts.filter(thought => {
      // Check if thought has milestone tag and is within timeframe
      const hasMilestoneTag = thought.tags && thought.tags.includes('Milestone');
      const isWithinTimeframe = thought.timestamp && thought.timestamp >= cutoffTimestamp;
      return hasMilestoneTag && isWithinTimeframe;
    });

    // Format milestone thoughts for chart annotations
    return milestoneThoughts.map(thought => {
      const date = new Date(thought.timestamp);
      const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const displayDate = date.toLocaleDateString('en-US', {
        month: 'short', 
        day: 'numeric'
      });

      console.log(thought);
      
      return {
        date: dateString,
        displayDate,
        thought: thought.thought,
        timestamp: thought.timestamp
      };
    });
  } catch (error) {
    console.error('Error getting milestone thoughts:', error);
    return [];
  }
};